import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { PublicQuestion, QuestionFeedback } from "@book2class/shared/learning";
import { api, ApiError, formatDate } from "../../api";
import { Markdown } from "../../components/markdown";
import { Spinner } from "../../components/ui";
import { NarrationPlayer, ReadAloud } from "../../components/extras";
import { ReportButton } from "../../components/report";
import { AssistantPanel, LearnCrumb, QuizRunner, VisualSummary, type Visual } from "./components";

type Lesson = {
  id: string;
  title: string;
  number: string;
  module: { id: string; position: number; title: string };
  objectives: string[];
  readingMinutes: number | null;
  introduction: string;
  sections: Array<{ heading: string; body: string }>;
  examples: Array<{ title: string; body: string }>;
  keyPoints: string[];
  summary: string;
  visual: Visual | null;
  state: "in_progress" | "completed";
  quiz: PublicQuestion[];
  lastQuizScore: number | null;
  notes: Array<{ id: string; body: string; createdAt: string }>;
  bookmarked: boolean;
  prev: { id: string; title: string; locked: boolean } | null;
  next: { id: string; title: string; locked: boolean } | null;
  audio: null | { url: string };
  video: null | {
    provider: "youtube" | "vimeo";
    embedUrl: string;
    watchUrl: string;
    title: string;
    watched: boolean;
  };
};

/** Envia o tempo de estudo a cada 30 s, só com a aba visível, e a rolagem máxima alcançada. */
function useStudyTimer(url: string | null) {
  const seconds = useRef(0);
  const scroll = useRef(0);
  useEffect(() => {
    if (!url) return;
    seconds.current = 0;
    const onScroll = () => {
      const h = document.documentElement;
      const pct = Math.round(((h.scrollTop + window.innerHeight) / Math.max(1, h.scrollHeight)) * 100);
      scroll.current = Math.max(scroll.current, Math.min(100, pct));
    };
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") seconds.current += 5;
    }, 5000);
    const flush = () => {
      if (!seconds.current) return;
      const body = { seconds: Math.min(600, seconds.current), scrollPercent: scroll.current };
      seconds.current = 0;
      void api.post(url, body).catch(() => undefined);
    };
    const send = setInterval(flush, 30_000);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      clearInterval(tick);
      clearInterval(send);
      window.removeEventListener("scroll", onScroll);
      flush();
    };
  }, [url]);
}

export function LessonPage() {
  const { courseId = "", lessonId = "" } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [courseTitle, setCourseTitle] = useState("");
  const [error, setError] = useState("");
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState<{
    next: string | null;
    finalAvailable: boolean;
    certificate: { code: string } | null;
  } | null>(null);

  const load = useCallback(() => {
    api
      .get<Lesson>(`/learn/${courseId}/lessons/${lessonId}`)
      .then((l) => {
        setDone(null);
        setLesson(l);
        window.scrollTo({ top: 0 });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar a aula."));
  }, [courseId, lessonId]);
  useEffect(load, [load]);
  useEffect(() => {
    api
      .get<{ course: { title: string } }>(`/learn/${courseId}`)
      .then((t) => setCourseTitle(t.course.title))
      .catch(() => undefined);
  }, [courseId]);
  useStudyTimer(lesson ? `/learn/${courseId}/lessons/${lessonId}/progress` : null);

  if (error)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
        <Link to={`/aluno/cursos/${courseId}`}>Voltar à trilha</Link>
      </main>
    );
  if (!lesson)
    return (
      <main className="page">
        <Spinner />
      </main>
    );

  async function complete() {
    setCompleting(true);
    try {
      const r = await api.post<{
        next: string | null;
        finalAvailable: boolean;
        certificate: { code: string } | null;
      }>(`/learn/${courseId}/lessons/${lessonId}/progress`, { completed: true });
      setDone(r);
      setLesson((l) => (l ? { ...l, state: "completed" } : l));
    } finally {
      setCompleting(false);
    }
  }

  return (
    <main className="page reader-page">
      <article className="reader">
        <LearnCrumb
          courseId={courseId}
          title={courseTitle || "Curso"}
          here={`Módulo ${lesson.module.position}`}
        />
        <header className="reader-head">
          <div className="eyebrow">Aula {lesson.number}</div>
          <h1>{lesson.title}</h1>
          <div className="reader-meta">
            <span>{lesson.readingMinutes ?? "?"} min de leitura</span>
            {lesson.state === "completed" && <span className="badge ok">Concluída</span>}
            <BookmarkButton
              courseId={courseId}
              lesson={lesson}
              onChange={(b) => setLesson({ ...lesson, bookmarked: b })}
            />
            <ReportButton courseId={courseId} itemType="lesson" itemId={lesson.id} itemLabel={lesson.title} />
            {lesson.audio ? (
              <NarrationPlayer key={lesson.id} src={lesson.audio.url} storageKey={lesson.id} />
            ) : (
              <ReadAloud
                key={lesson.id}
                getText={() =>
                  [
                    lesson.title,
                    lesson.introduction,
                    ...lesson.sections.flatMap((x) => [x.heading, x.body]),
                    ...lesson.examples.flatMap((x) => [`Exemplo: ${x.title}`, x.body]),
                    lesson.keyPoints.length ? `Pontos-chave: ${lesson.keyPoints.join(". ")}` : "",
                    lesson.summary ? `Resumo: ${lesson.summary}` : "",
                  ].join("\n")
                }
              />
            )}
          </div>
          {lesson.objectives.length > 0 && (
            <div className="objectives">
              <strong>Nesta aula, você vai:</strong>
              <ul>
                {lesson.objectives.map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
            </div>
          )}
        </header>

        {lesson.video && (
          <VideoBlock
            courseId={courseId}
            lessonId={lesson.id}
            video={lesson.video}
            onWatched={() => setLesson({ ...lesson, video: { ...lesson.video!, watched: true } })}
          />
        )}
        <Markdown text={lesson.introduction} />
        {lesson.sections.map((s, i) => (
          <section key={i}>
            <h2 className="reader-h2">{s.heading}</h2>
            <Markdown text={s.body} />
          </section>
        ))}
        {lesson.examples.map((e, i) => (
          <aside key={i} className="example">
            <div className="eyebrow">Exemplo</div>
            <strong>{e.title}</strong>
            <Markdown text={e.body} />
          </aside>
        ))}
        {lesson.keyPoints.length > 0 && (
          <aside className="keypoints">
            <div className="eyebrow">Pontos-chave</div>
            <ul>
              {lesson.keyPoints.map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
          </aside>
        )}
        <VisualSummary visual={lesson.visual} />
        {lesson.summary && (
          <section>
            <h2 className="reader-h2">Resumo</h2>
            <Markdown text={lesson.summary} />
          </section>
        )}

        {lesson.quiz.length > 0 && (
          <section className="card card-pad quiz-card">
            <div className="eyebrow">
              Fixação{lesson.lastQuizScore !== null ? ` · última nota ${lesson.lastQuizScore}%` : ""}
            </div>
            <h2 style={{ fontSize: "1.2rem" }}>Teste o que aprendeu</h2>
            <QuizRunner
              key={lesson.id}
              reportCourseId={courseId}
              questions={lesson.quiz}
              submit={(answers) =>
                api.post<{ score: number; feedback: QuestionFeedback[] }>(
                  `/learn/${courseId}/lessons/${lessonId}/quiz`,
                  { answers },
                )
              }
            />
          </section>
        )}

        <div className="complete-box card card-pad">
          {done ? (
            <>
              <strong>Aula concluída!</strong>
              {done.certificate ? (
                <p>Parabéns, você concluiu o curso. Seu certificado já está disponível.</p>
              ) : done.next ? (
                <p className="segment-meta">A próxima aula foi liberada.</p>
              ) : done.finalAvailable ? (
                <p className="segment-meta">Você concluiu todas as aulas. A avaliação final foi liberada.</p>
              ) : null}
              <div className="actions" style={{ justifyContent: "flex-start" }}>
                {done.next && (
                  <button
                    className="btn"
                    onClick={() => navigate(`/aluno/cursos/${courseId}/aulas/${done.next}`)}
                  >
                    Próxima aula
                  </button>
                )}
                {!done.next && done.finalAvailable && !done.certificate && (
                  <Link className="btn" to={`/aluno/cursos/${courseId}/avaliacao`}>
                    Ir para a avaliação final
                  </Link>
                )}
                <Link className="btn ghost" to={`/aluno/cursos/${courseId}`}>
                  Voltar à trilha
                </Link>
              </div>
            </>
          ) : lesson.state === "completed" ? (
            <p className="segment-meta" style={{ margin: 0 }}>
              Você já concluiu esta aula.
            </p>
          ) : (
            <>
              <p className="segment-meta" style={{ margin: 0 }}>
                Leu a aula e fez o quiz? Marque a aula como concluída para avançar na trilha.
              </p>
              <button className="btn" disabled={completing} onClick={() => void complete()}>
                {completing ? "Salvando…" : "Concluir aula"}
              </button>
            </>
          )}
        </div>

        <nav className="pager reader-nav" aria-label="Navegação entre aulas">
          {lesson.prev ? (
            <Link className="btn ghost small" to={`/aluno/cursos/${courseId}/aulas/${lesson.prev.id}`}>
              ← {lesson.prev.title}
            </Link>
          ) : (
            <span />
          )}
          {lesson.next && !lesson.next.locked && (
            <Link className="btn ghost small" to={`/aluno/cursos/${courseId}/aulas/${lesson.next.id}`}>
              {lesson.next.title} →
            </Link>
          )}
        </nav>
        <Comments key={lesson.id} courseId={courseId} lessonId={lesson.id} />
      </article>
      <aside className="reader-side">
        <NotesPanel
          courseId={courseId}
          lesson={lesson}
          onChange={(notes) => setLesson({ ...lesson, notes })}
        />
      </aside>
      <AssistantPanel courseId={courseId} />
    </main>
  );
}

function BookmarkButton({
  courseId,
  lesson,
  onChange,
}: {
  courseId: string;
  lesson: Lesson;
  onChange: (b: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`btn ghost small${lesson.bookmarked ? " on" : ""}`}
      aria-pressed={lesson.bookmarked}
      onClick={async () => {
        const r = await api.put<{ bookmarked: boolean }>(`/learn/${courseId}/lessons/${lesson.id}/bookmark`, {
          on: !lesson.bookmarked,
        });
        onChange(r.bookmarked);
      }}
    >
      {lesson.bookmarked ? "★ Marcada" : "☆ Marcar aula"}
    </button>
  );
}

function NotesPanel({
  courseId,
  lesson,
  onChange,
}: {
  courseId: string;
  lesson: Lesson;
  onChange: (n: Lesson["notes"]) => void;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <section className="card card-pad notes">
      <h2 style={{ fontSize: "1rem" }}>Minhas anotações</h2>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!body.trim()) return;
          setBusy(true);
          const n = await api.post<Lesson["notes"][number]>(`/learn/${courseId}/lessons/${lesson.id}/notes`, {
            body,
          });
          onChange([...lesson.notes, n]);
          setBody("");
          setBusy(false);
        }}
      >
        <textarea
          rows={3}
          aria-label="Nova anotação"
          placeholder="Anote algo desta aula…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button className="btn small" disabled={busy || !body.trim()} style={{ marginTop: 8 }}>
          Salvar anotação
        </button>
      </form>
      <ul className="note-list">
        {lesson.notes.map((n) => (
          <li key={n.id}>
            <p>{n.body}</p>
            <div className="segment-meta">
              {formatDate(n.createdAt)} ·{" "}
              <button
                type="button"
                className="link-btn"
                onClick={async () => {
                  await api.del(`/learn/${courseId}/notes/${n.id}`);
                  onChange(lesson.notes.filter((x) => x.id !== n.id));
                }}
              >
                excluir
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

type Comment = {
  id: string;
  parentId: string | null;
  body: string;
  author: string;
  fromTeacher: boolean;
  mine: boolean;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
};

/** Comentários da aula: publicados após a moderação do professor. */
function Comments({ courseId, lessonId }: { courseId: string; lessonId: string }) {
  const [data, setData] = useState<{ enabled: boolean; comments: Comment[] } | null>(null);
  const [body, setBody] = useState("");
  const [msg, setMsg] = useState("");
  const url = `/learn/${courseId}/lessons/${lessonId}/comments`;
  const load = useCallback(() => {
    api
      .get<{ enabled: boolean; comments: Comment[] }>(url)
      .then(setData)
      .catch(() => setData({ enabled: false, comments: [] }));
  }, [url]);
  useEffect(load, [load]);
  if (!data?.enabled) return null;
  const roots = data.comments.filter((c) => !c.parentId);
  const replies = (id: string) => data.comments.filter((c) => c.parentId === id);
  const Item = ({ c }: { c: Comment }) => (
    <div className={`comment${c.fromTeacher ? " teacher" : ""}`}>
      <div className="segment-meta">
        <strong>{c.mine ? "Você" : c.author}</strong>
        {c.fromTeacher ? " · professor(a)" : ""} · {formatDate(c.createdAt)}
        {c.status === "pending"
          ? " · aguardando moderação"
          : c.status === "rejected"
            ? " · não publicado"
            : ""}
      </div>
      <p>{c.body}</p>
    </div>
  );
  return (
    <section className="card card-pad comments">
      <h2 style={{ fontSize: "1.1rem" }}>Comentários ({roots.length})</h2>
      {roots.length === 0 && <p className="segment-meta">Seja o primeiro a comentar esta aula.</p>}
      <ul className="comment-list">
        {roots.map((c) => (
          <li key={c.id}>
            <Item c={c} />
            {replies(c.id).map((r) => (
              <div key={r.id} className="comment-reply">
                <Item c={r} />
              </div>
            ))}
          </li>
        ))}
      </ul>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post(url, { body });
            setBody("");
            setMsg("Comentário enviado. Ele aparece para a turma após a aprovação do professor.");
            load();
          } catch (err) {
            setMsg(err instanceof ApiError ? err.message : "Não foi possível enviar.");
          }
        }}
      >
        <textarea
          rows={2}
          aria-label="Escreva um comentário"
          placeholder="Compartilhe uma dúvida ou reflexão sobre a aula…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="actions" style={{ justifyContent: "space-between" }}>
          <span className="segment-meta">{msg}</span>
          <button className="btn small" disabled={body.trim().length < 2}>
            Comentar
          </button>
        </div>
      </form>
    </section>
  );
}

/** Vídeo do professor (YouTube ou Vimeo) no topo da aula. */
function VideoBlock({
  courseId,
  lessonId,
  video,
  onWatched,
}: {
  courseId: string;
  lessonId: string;
  video: NonNullable<Lesson["video"]>;
  onWatched: () => void;
}) {
  return (
    <figure className="video-block">
      <div className="video-frame">
        <iframe
          src={video.embedUrl}
          title={video.title}
          loading="lazy"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      <figcaption className="row-between">
        <span className="segment-meta">
          🎬 {video.title} · {video.provider === "youtube" ? "YouTube" : "Vimeo"} ·{" "}
          <a href={video.watchUrl} target="_blank" rel="noreferrer">
            abrir em nova aba
          </a>
        </span>
        {video.watched ? (
          <span className="badge ok">Vídeo assistido</span>
        ) : (
          <button
            type="button"
            className="btn ghost small"
            onClick={async () => {
              await api.post(`/learn/${courseId}/lessons/${lessonId}/video-watched`).catch(() => undefined);
              onWatched();
            }}
          >
            Marcar vídeo como assistido
          </button>
        )}
      </figcaption>
    </figure>
  );
}
